import { Button, type ModalProps } from '@omnipresentgroup/design-system';
import { XERO_CONNECT_PATH } from '@omnipresentgroup/domains';

import { IntegrationWidgetModal } from '../../../components';
import { useManagerUser } from '../../../hooks';
import { XERO_FAQ_LINK } from '../constants';

const INTEGRATION_STEPS = [
  {
    id: 1,
    text: `Click the "Connect to Xero" button. You will be redirected to Xero's login page. Enter your Xero credentials and sign in.`,
  },
  {
    id: 2,
    text: 'Review the permissions to access your Xero data (e.g., read/write invoices, access account codes). Click "Allow Access" to proceed.',
  },
  {
    id: 3,
    text: 'If you manage multiple organisations in Xero, choose the organisation you want to connect to OmniPlatform.',
  },
  {
    id: 4,
    text: 'Once connected, OmniPlatform may prompt you to configure specific settings, such as mapping account codes, selecting data to sync, or defining workflows.',
  },
  {
    id: 5,
    text: `Save your settings, and you're done! The integration is now active.`,
  },
];

const WHAT_XERO_DESCRIPTION =
  'Integrate your Xero account to streamline your financial data management directly from OmniPlatform. With this integration, you can sync invoice data effortlessly.';
const WHY_XERO_DESCRIPTION =
  'Eliminate manual data entry and reduce the risk of errors. Keep Xero as your source of truth for all financial operations, while OmniPlatform ensures seamless data flow in the background.';
const CONNECT_BUTTON_LABEL = 'Connect to Xero';

type XeroIntegrationModalProps = {
  modalProps: Omit<ModalProps, 'children'>;
  handleClose: () => void;
  canConfigure: boolean;
  isNotConnected: boolean;
};

export const XeroIntegrationModal = ({
  modalProps,
  handleClose,
  canConfigure,
  isNotConnected,
}: XeroIntegrationModalProps) => {
  const { companyId } = useManagerUser();

  return (
    <IntegrationWidgetModal
      integrationName="Xero"
      whatDescription={WHAT_XERO_DESCRIPTION}
      whyDescription={WHY_XERO_DESCRIPTION}
      steps={canConfigure && isNotConnected ? INTEGRATION_STEPS : undefined}
      modalProps={modalProps}
      handleClose={handleClose}
      faqsLink={XERO_FAQ_LINK}
      action={
        canConfigure && isNotConnected ? (
          <Button
            onClick={() => window.open(XERO_CONNECT_PATH(companyId), '_self')}
          >
            {CONNECT_BUTTON_LABEL}
          </Button>
        ) : undefined
      }
    />
  );
};
